import type { ReactNode } from 'react';
import { cx } from '../../../ui/cx';
import { tableStyles } from '../../../ui/Table';

export interface BatchColumn<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'start' | 'end';
  /** The card title on phones (no label). */
  isPrimary?: boolean;
}

/**
 * A native, read-only table for the batch page's review and results (BLOCK-1B-BULK-DESIGN §7): a
 * caption, one `thead`, ONE `tbody` (never grouped, no `tfoot`, AC-B9), and on phones the same
 * stacked cards as the register (`ui/Table` styles). Rows carry their own data attributes.
 */
export function BatchTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  rowProps,
  testId,
}: {
  caption: string;
  columns: BatchColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  rowProps?: (row: T) => Record<string, string | number | undefined>;
  testId: string;
}) {
  return (
    <div className={tableStyles.frame} data-testid={testId}>
      <div className={tableStyles.scroller}>
        <table className={tableStyles.table}>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cx(column.align === 'end' && tableStyles.end)}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} {...(rowProps?.(row) ?? {})}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cx(
                      column.align === 'end' && tableStyles.end,
                      column.isPrimary && tableStyles.primaryCell,
                    )}
                  >
                    <span className={tableStyles.cellLabel}>{column.header}</span>
                    <span>{column.cell(row)}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
